import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Template } from '../components/interfaces/template.interface';
import dcmmApi from '../utils/httpClient';
import { ApiSuccess } from '../utils/api-response';

interface TemplatesState {
  templates: Template[];
  loading: boolean;
  error: string | null;
}

const initialState: TemplatesState = {
  templates: [],
  loading: false,
  error: null,
};

// Async thunks for API calls
export const fetchTemplates = createAsyncThunk(
  'templates/fetchTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await dcmmApi.get<ApiSuccess<Template[]>>(`${process.env.REACT_APP_API_URL}/templates/getAll`);
      return response.data.data;
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch templates.');
      return rejectWithValue(error.response?.data || 'Failed to fetch templates');
    }
  }
);

export const addTemplate = createAsyncThunk(
  'templates/addTemplate',
  async (newTemplate: Template, { rejectWithValue }) => {
    try {
      const response = await dcmmApi.post<ApiSuccess>(`${process.env.REACT_APP_API_URL}/templates/create`, newTemplate);
      toast.success('Template added successfully!');
      return response.data.data;
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to add template.');
      return rejectWithValue(error.response?.data || 'Failed to add template');
    }
  }
);

export const deleteTemplate = createAsyncThunk(
  'templates/deleteTemplate',
  async (id: string, { rejectWithValue }) => {
    try {
      await dcmmApi.delete<ApiSuccess>(`${process.env.REACT_APP_API_URL}/templates/delete/${id}`);
      toast.success('Template deleted successfully!');
      return id;
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete template.');
      return rejectWithValue(error.response?.data || 'Failed to delete template');
    }
  }
);

export const updateTemplate = createAsyncThunk(
  'templates/updateTemplate',
  async ({ id, updatedTemplate }: { id: string; updatedTemplate: Template }, { rejectWithValue }) => {
    try {
      const response = await dcmmApi.put<ApiSuccess>(`${process.env.REACT_APP_API_URL}/templates/update/${id}`, updatedTemplate);
      toast.success('Template updated successfully!');
      return response.data.data;
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update template.');
      return rejectWithValue(error.response?.data || 'Failed to update template');
    }
  }
);

export const publishTemplate = createAsyncThunk(
  'templates/publishTemplate',
  async (id: string, { rejectWithValue }) => {
    try {
      // backend endpoint: POST /templates/publish/:id
      const response = await dcmmApi.post<ApiSuccess>(
        `${process.env.REACT_APP_API_URL}/templates/publish/${id}`,
        {}
      );

      toast.success('Template published successfully!');
      return response.data.data; // published Template returned from backend
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to publish template.');
      return rejectWithValue(error.response?.data || 'Failed to publish template');
    }
  }
);


const templatesSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchTemplates.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchTemplates.fulfilled, (state, action) => {
      state.loading = false;
      state.templates = action.payload;
    });
    builder.addCase(fetchTemplates.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    builder.addCase(addTemplate.fulfilled, (state, action) => {
      state.templates.unshift(action.payload);
    });

    builder.addCase(deleteTemplate.fulfilled, (state, action) => {
      state.templates = state.templates.filter((template) => template._id !== action.payload);
    });

    builder.addCase(updateTemplate.fulfilled, (state, action) => {
      const index = state.templates.findIndex((template) => template._id === action.payload._id);
      if (index !== -1) {
        state.templates[index] = action.payload;
      }
    });

    builder.addCase(publishTemplate.fulfilled, (state, action) => {
      const index = state.templates.findIndex((t) => t._id === action.payload._id);
      if (index !== -1) {
        state.templates[index] = action.payload;
      }
    });
    
  },
});

export default templatesSlice.reducer;
